import mixpanel from 'mixpanel-browser';
import { buildSessionReplayInitOptions } from '@satellite/analytics/sessionReplay';

class MixpanelPluginV2 {
  /** Tail of the Session Replay lifecycle queue — see #queueSessionRecording. */
  #sessionRecordingLifecycle = Promise.resolve();

  /**
   * @param {Object} options
   * @param {string | null} [options.token]
   * @param {boolean} [options.enabled]
   * @param {boolean} [options.debug]
   * @param {Object} [options.logger]
   */
  constructor({ token = null, enabled = true, debug = false, logger = console } = {}) {
    this.client = mixpanel;
    this.token = token;
    this.enabled = enabled;
    this.debug = debug;
    this.logger = logger;
    this.isInitialized = false;
    /**
     * Whether identify() has ever succeeded. reset() is a no-op until it has, so
     * apps can call reset() unconditionally on a null user without side effects.
     */
    this.hasIdentified = false;
  }

  /**
   * Reconfigure plugin at runtime.
   * @param {Object} options
   * @param {string | null} [options.token]
   * @param {boolean} [options.enabled]
   * @param {boolean} [options.debug]
   */
  configure({ token, enabled, debug } = {}) {
    if (token !== undefined) {
      this.token = token;
    }
    if (enabled !== undefined) {
      this.enabled = enabled;
    }
    if (debug !== undefined) {
      this.debug = debug;
    }
  }

  /**
   * Initialize analytics SDK.
   *
   * Pass `sessionReplay` to opt this app into Session Replay recording. The plugin
   * composes the quota- and privacy-protecting SDK options itself, so no app needs
   * to know them. Those options are applied last and therefore cannot be overridden
   * by `initOptions` — record_sessions_percent must stay 0 so the gate, not the SDK,
   * owns every sampling decision.
   *
   * @param {Object} options
   * @param {string} [options.token]
   * @param {Object} [options.initOptions]
   * @param {{ recordMaxMs: number, recordIdleTimeoutMs: number }} [options.sessionReplay]
   * @returns {boolean}
   */
  init({ token, initOptions = {}, sessionReplay } = {}) {
    if (token !== undefined) {
      this.token = token;
    }

    if (!this.#canUseClient() || !this.token || typeof this.client.init !== 'function') {
      this.isInitialized = false;
      if (this.debug && this.#canUseClient() && !this.token) {
        this.logger.error('[MixpanelPluginV2] init skipped: no token (events will not be sent)');
      }
      return false;
    }

    const resolvedInitOptions = sessionReplay
      ? { ...initOptions, ...buildSessionReplayInitOptions(sessionReplay) }
      : initOptions;

    try {
      this.client.init(this.token, resolvedInitOptions);
      this.isInitialized = true;
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] init error', error);
      this.isInitialized = false;
    }
    return this.isInitialized;
  }

  /**
   * Track a generic analytics event.
   * @param {string} event
   * @param {Object} [payload]
   */
  track(event, payload = {}) {
    const canTrack = this.#canTrack() && typeof this.client.track === 'function';
    if (this.debug) {
      this.logger.info('[MixpanelPluginV2] track', {
        event,
        payload,
        skipped: !canTrack
      });
    }

    if (!canTrack) {
      return;
    }

    try {
      this.client.track(event, payload);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] track error', error);
    }
  }

  /**
   * Set distinct user identity.
   * @param {string} distinctId
   */
  identify(distinctId) {
    if (!this.#canTrack() || typeof this.client.identify !== 'function' || !distinctId) {
      return;
    }
    try {
      this.client.identify(distinctId);
      this.hasIdentified = true;
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] identify error', error);
    }
  }

  /**
   * Set user profile properties.
   * Maps to mixpanel.people.set when available.
   * @param {Object} properties
   */
  setUserProperties(properties = {}) {
    if (!this.#canTrack() || !this.client.people || typeof this.client.people.set !== 'function') {
      return;
    }

    try {
      this.client.people.set(properties);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setUserProperties error', error);
    }
  }

  /**
   * Register super properties for future events.
   * Maps to mixpanel.register.
   * @param {Object} properties
   */
  setProperties(properties = {}) {
    if (!this.#canTrack() || typeof this.client.register !== 'function') {
      return;
    }

    try {
      this.client.register(properties);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setProperties error', error);
    }
  }

  /**
   * Set group-level properties.
   * Maps to mixpanel.get_group(...).set(...).
   * @param {string} groupKey
   * @param {string | number} groupId
   * @param {Object} properties
   */
  setGroupProperties(groupKey, groupId, properties = {}) {
    if (!this.#canTrack() || typeof this.client.get_group !== 'function') {
      return;
    }

    try {
      const group = this.client.get_group(groupKey, groupId);
      if (group && typeof group.set === 'function') {
        group.set(properties);
      }
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setGroupProperties error', error);
    }
  }

  /**
   * Register org super properties (org_id, org_name).
   * @param {{ id?: string | null, name?: string | null }} org
   */
  setOrgProperties(org) {
    this.setProperties({ org_id: org?.id ?? null, org_name: org?.name ?? null });
  }

  /**
   * Register warehouse super properties (warehouse_id, warehouse_name).
   * @param {{ id?: string | null, name?: string | null }} warehouse
   */
  setWarehouseProperties(warehouse) {
    this.setProperties({
      warehouse_id: warehouse?.id ?? null,
      warehouse_name: warehouse?.name ?? null
    });
  }

  /**
   * Reset the Mixpanel instance, clearing user identity and super properties.
   *
   * No-ops until identify() has succeeded at least once. Apps watch their auth
   * store with `{ immediate: true }`, so the watcher fires with a null user on
   * every page load, before /auth/me resolves. Resetting there would generate a
   * fresh anonymous distinct_id each load and — since MixpanelLib.reset() calls
   * stop_session_recording() — would fight the SDK's resume of this tab's replay.
   * Guarding here keeps that logic out of every app's watcher.
   *
   * @returns {void}
   */
  reset() {
    if (!this.hasIdentified) {
      return;
    }

    if (!this.#canUseClient() || !this.isInitialized || typeof this.client.reset !== 'function') {
      return;
    }

    try {
      this.client.reset();
      this.hasIdentified = false;
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] reset error', error);
    }

    /**
     * MixpanelLib.reset() stops the recording synchronously, which a start still waiting on the
     * recorder bundle would outlive — it would begin recording after logout, under the fresh
     * anonymous distinct_id. Enqueueing a stop puts it behind any pending start.
     *
     * Fire-and-forget on purpose: reset() stays synchronous for its callers (a Vuex logout
     * action, a router guard), and stopSessionRecording() never rejects.
     */
    this.stopSessionRecording();
  }

  /**
   * Start a Mixpanel Session Replay recording.
   *
   * Callers MUST gate eligibility before calling this — the gate in
   * satellite/analytics/sessionReplay.ts owns that decision.
   * Idempotent per tab: the SDK resumes an existing recording when the tab id
   * matches and the replay has not expired.
   *
   * Returns a promise-wrapped boolean; rejection is caught internally and never
   * propagates to the caller (the call site is a router hook).
   * @returns {Promise<boolean>}
   */
  async startSessionRecording() {
    if (!this.#canTrack() || typeof this.client.start_session_recording !== 'function') {
      return false;
    }
    return this.#queueSessionRecording('startSessionRecording', () =>
      this.client.start_session_recording()
    );
  }

  /**
   * Stop the current Session Replay recording.
   *
   * Unlike reset(), this only ends the recording — identity and super properties
   * are untouched. Used by apps that record a bounded funnel rather than a
   * sampled session (e.g. Luna records only the carrier registration flow).
   * @returns {Promise<boolean>} whether the SDK call completed without throwing
   */
  async stopSessionRecording() {
    if (!this.#canTrack() || typeof this.client.stop_session_recording !== 'function') {
      return false;
    }
    return this.#queueSessionRecording('stopSessionRecording', () =>
      this.client.stop_session_recording()
    );
  }

  /**
   * Pause the current Session Replay recording, keeping it resumable.
   *
   * Intended for privacy-sensitive moments inside an otherwise recorded flow —
   * e.g. while a password field is toggled visible.
   * @returns {Promise<boolean>} whether the SDK call completed without throwing
   */
  async pauseSessionRecording() {
    if (!this.#canTrack() || typeof this.client.pause_session_recording !== 'function') {
      return false;
    }
    return this.#queueSessionRecording('pauseSessionRecording', () =>
      this.client.pause_session_recording()
    );
  }

  /**
   * Resume a Session Replay recording previously paused by pauseSessionRecording().
   * @returns {Promise<boolean>} whether the SDK call completed without throwing
   */
  async resumeSessionRecording() {
    if (!this.#canTrack() || typeof this.client.resume_session_recording !== 'function') {
      return false;
    }
    return this.#queueSessionRecording('resumeSessionRecording', () =>
      this.client.resume_session_recording()
    );
  }

  /**
   * Returns the current Mixpanel replay id for this tab, or null if not recording.
   * @returns {string | null}
   */
  getSessionReplayId() {
    if (!this.#canTrack() || typeof this.client.get_session_recording_properties !== 'function') {
      return null;
    }
    try {
      return this.client.get_session_recording_properties()?.$mp_replay_id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns a direct URL to the current replay in Mixpanel, or null if not recording.
   * Uses the SDK's dedicated get_session_replay_url() method — get_session_recording_properties()
   * only returns $mp_replay_id in mixpanel-browser 2.79+.
   * @returns {string | null}
   */
  getSessionReplayUrl() {
    if (!this.#canTrack() || typeof this.client.get_session_replay_url !== 'function') {
      return null;
    }
    try {
      return this.client.get_session_replay_url() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns true when there is an active replay id for this tab.
   * @returns {boolean}
   */
  isSessionReplayActive() {
    return Boolean(this.getSessionReplayId());
  }

  /**
   * Runs Session Replay lifecycle calls one at a time, in call order.
   *
   * The SDK loads its recorder bundle asynchronously, and RecorderManager's stop/pause/resume
   * are no-ops that resolve immediately while `_recorder` is still null. Issuing them straight
   * away would therefore let an in-flight start win, and recording would cover exactly the
   * interval the caller meant to exclude — a password field toggled visible, say. Chaining
   * every call keeps the SDK's own ordering honest.
   *
   * Never rejects: failures are logged and reported as `false`, so callers in `mounted()`
   * hooks need neither await nor catch.
   *
   * @param {string} label method name, for the error log
   * @param {() => unknown} operation the SDK call, sync or promise-returning
   * @returns {Promise<boolean>}
   */
  #queueSessionRecording(label, operation) {
    const queued = this.#sessionRecordingLifecycle.then(async () => {
      try {
        await operation();
        return true;
      } catch (error) {
        this.logger.error(`[MixpanelPluginV2] ${label} error`, error);
        return false;
      }
    });

    // `queued` already swallowed its own failure, so the chain can never end up rejected and
    // one failed call cannot stall the ones behind it.
    this.#sessionRecordingLifecycle = queued;

    return queued;
  }

  #canUseClient() {
    return this.enabled && !!this.client;
  }

  #canTrack() {
    return this.#canUseClient() && this.isInitialized;
  }
}

export default MixpanelPluginV2;
